import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { MagicWand, Plus } from "@phosphor-icons/react";
import type { ResumeDto } from "@reactive-resume/dto";
import { createResumeSchema } from "@reactive-resume/dto";
import { idSchema, sampleResume } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
} from "@reactive-resume/ui";
import { cn, generateRandomName } from "@reactive-resume/utils";
import slugify from "@sindresorhus/slugify";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";


import { useCreateResume, useDeleteResume, useUpdateResume } from "@/client/services/resume";
import { useImportResume } from "@/client/services/resume/import";

// 使用浏览器原生crypto API生成UUID
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // 降级方案：生成随机字符串
  return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
};
import { useDialog } from "@/client/stores/dialog";
const formSchema = createResumeSchema.extend({ id: idSchema.optional(), slug: z.string() });

type FormValues = z.infer<typeof formSchema>;

export const ResumeDialog = () => {
  const { isOpen, mode, payload, close } = useDialog<ResumeDto>("resume");
const navigate = useNavigate();
  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  const { createResume, loading: createLoading } = useCreateResume();
  const { updateResume, loading: updateLoading } = useUpdateResume();
  const { deleteResume, loading: deleteLoading } = useDeleteResume();
  const { importResume: duplicateResume, loading: duplicateLoading } = useImportResume();

  const loading = createLoading || updateLoading || deleteLoading || duplicateLoading;

  const zhErrorMap: z.ZodErrorMap = (issue, ctx) => {
    if (issue.code === "too_small" && issue.minimum === 1 && issue.type === "string") {
      if (issue.path?.[0] === "title") return { message: t`请输入简历名称` };
      return { message: t`此字段为必填项` };
    }
    return { message: ctx.defaultError };
  };

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema, { errorMap: zhErrorMap }),
    defaultValues: { title: "", slug: "" },
  });

  useEffect(() => {
    if (isOpen) {
      onReset();
    }
  }, [isOpen, payload]);

  useEffect(() => {
    // 只在更新模式下根据标题自动生成slug，复制模式保持已设置的slug
    if (isUpdate) {
      const slug = slugify(form.watch("title"));
      form.setValue("slug", slug);
    }
  }, [form.watch("title"), isUpdate]);

  const onSubmit = async (values: FormValues) => {
    if (isCreate) {
      // 创建时直接生成“样例简历”，名称与 slug 来自输入/自动生成
      const res: ResumeDto = await duplicateResume({
        title: values.title,
        slug: values.slug,
        data: sampleResume,
      });
      // 路由跳转
      navigate(`/builder/${res.id}`);
    }

    if (isUpdate) {
      if (!payload.item?.id) return;

      await updateResume({
        id: payload.item.id,
        title: values.title,
        slug: values.slug,
      });
    }

    if (isDuplicate) {
      if (!payload.item?.id) return;

      await duplicateResume({
        title: values.title,
        slug: values.slug,
        data: payload.item.data,
      });
    }

    if (isDelete) {
      if (!payload.item?.id) return;

      await deleteResume({ id: payload.item.id });
    }

    close();
  };

  const onReset = () => {
    if (isCreate) {
      // 创建模式下重置标题但保持自动生成的slug
      const uniqueId = generateUUID().slice(0, 8);
      const uniqueSlug = `resume-${uniqueId}`;
      form.reset({ title: "", slug: uniqueSlug });
    }
    if (isUpdate)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
    if (isDuplicate) {
      // 复制模式下生成带唯一标识的slug
      const uniqueId = generateUUID().slice(0, 8);
      const baseSlug = payload.item?.slug || "resume";
      const uniqueSlug = `${baseSlug}-copy-${uniqueId}`;
      form.reset({ 
        title: `${payload.item?.title} (Copy)`, 
        slug: uniqueSlug 
      });
    }
    if (isDelete)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
  };

  const onChangeTitle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const title = e.target.value;
    form.setValue("title", title);
    
    // 自动生成唯一Slug，使用uuid确保唯一性
    const uniqueId = generateUUID().slice(0, 8);
    const baseSlug = title ? slugify(title) : "resume";
    const uniqueSlug = `${baseSlug}-${uniqueId}`;
    form.setValue("slug", uniqueSlug);
  };

  const onGenerateRandomName = () => {
    const name = generateRandomName();
    const uniqueId = generateUUID().slice(0, 8);
    const baseSlug = slugify(name) || "resume";
    const uniqueSlug = `${baseSlug}-${uniqueId}`;
    
    form.setValue("title", name);
    form.setValue("slug", uniqueSlug);
  };

  const onCreateSample = async () => {
    const randomName = generateRandomName();
    const uniqueId = generateUUID().slice(0, 8);
    const baseSlug = slugify(randomName) || "resume";
    const uniqueSlug = `${baseSlug}-${uniqueId}`;

    const res: ResumeDto = await duplicateResume({
      title: randomName,
      slug: uniqueSlug,
      data: sampleResume,
    });
    close();
    // 路由跳转
    navigate(`/builder/${res.id}`);
  };

  if (isDelete) {
    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete your resume?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone. This will permanently delete your resume and cannot be recovered.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)}>
                  {t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent>
        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
              <DialogTitle>
                <div className="flex items-center space-x-2.5">
                  <Plus />
                  <h2>
                    {isCreate && t`Create Sample Resume`}
                    {isUpdate && t`Update an existing resume`}
                    {isDuplicate && t`Duplicate an existing resume`}
                  </h2>
                </div>
              </DialogTitle>
              <DialogDescription>
                {isUpdate && t`Changed your mind about the name? Give it a new one.`}
                {isDuplicate && t`Give your old resume a new name.`}
              </DialogDescription>
            </DialogHeader>

            <FormField
              name="title"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Title`}</FormLabel>
                  <FormControl>
                    <div className="flex items-center justify-between gap-x-2">
                      <Input {...field} onChange={onChangeTitle} className="flex-1" />

                      {(isCreate || isDuplicate) && (
                        <Tooltip content={t`Generate a random title for your resume`}>
                          <Button
                            size="icon"
                            type="button"
                            variant="outline"
                            onClick={onGenerateRandomName}
                          >
                            <MagicWand />
                          </Button>
                        </Tooltip>
                      )}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Slug字段隐藏，自动生成 */}
            <FormField
              name="slug"
              control={form.control}
              render={({ field }) => (
                <FormItem className="hidden">
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <div className="flex items-center">
                <Button type="submit" disabled={loading}>
                  {isCreate && t`Create Sample Resume`}
                  {isUpdate && t`Save Changes`}
                  {isDuplicate && t`Duplicate`}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
